import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bulma/css/bulma.css';
import './styles.scss';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Home from './Home';
import RoutinePage from './components/RoutinePage';
import SettingsPage from './components/SettingsPage';
import {
  createRoutine,
  STORAGE_KEY,
  clone,
  getActiveRoutine,
  loadConfig,
  parseTasks,
} from './components/utils';

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Shared session state ──────────────────────────────────────────────────
  const [config, setConfig] = useState(() => loadConfig());

  // ── Settings form state ───────────────────────────────────────────────────
  const [setTitle, setSetTitle] = useState('');
  const [setHour, setSetHour] = useState('');
  const [setMinute, setSetMinute] = useState('');
  const [setTasks, setSetTasks] = useState('');
  const [setRoutineId, setSetRoutineId] = useState('');
  const [setChildrenDraft, setSetChildrenDraft] = useState([]);

  const activeRoutine = useMemo(() => getActiveRoutine(config), [config]);

  const findRoutineById = useCallback(
    (routineId) => config.routines.find((item) => item.id === routineId),
    [config.routines],
  );

  // Sync activeRoutineId when navigating directly to a routine URL
  useEffect(() => {
    const match = location.pathname.match(/^\/routine\/([^/]+)/);
    if (!match) return;
    const routeRoutineId = decodeURIComponent(match[1]);
    const routeRoutine = findRoutineById(routeRoutineId);
    if (!routeRoutine || config.activeRoutineId === routeRoutineId) return;
    const nextConfig = { ...config, activeRoutineId: routeRoutineId };
    setConfig(nextConfig);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextConfig));
  }, [config, findRoutineById, location.pathname]);

  // Re-load settings form when opening settings from any route
  const loadRoutineIntoSettings = useCallback((routine) => {
    setSetTitle(routine.title);
    setSetHour(String(routine.hour));
    setSetMinute(String(routine.minute));
    setSetTasks(routine.tasks.map((t) => `${t.icon}|${t.title}`).join(','));
    setSetChildrenDraft(clone(routine.children));
    setSetRoutineId(routine.id);
  }, []);

  useEffect(() => {
    if (location.pathname !== '/settings') return;
    loadRoutineIntoSettings(activeRoutine);
  }, [activeRoutine, loadRoutineIntoSettings, location.pathname]);

  // ── Session actions ───────────────────────────────────────────────────────
  const startRoutine = useCallback((routineId) => {
    const routine = config.routines.find((item) => item.id === routineId) || activeRoutine;
    if (config.activeRoutineId !== routine.id) {
      const nextConfig = { ...config, activeRoutineId: routine.id };
      setConfig(nextConfig);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextConfig));
    }
    navigate(`/routine/${routine.id}`);
  }, [activeRoutine, config, navigate]);

  // ── Settings actions ──────────────────────────────────────────────────────
  const openSettings = useCallback((returnTo = '/') => {
    loadRoutineIntoSettings(activeRoutine);
    navigate('/settings', { state: { returnTo } });
  }, [activeRoutine, loadRoutineIntoSettings, navigate]);

  const selectRoutineInSettings = (routineId) => {
    const routine = config.routines.find((item) => item.id === routineId);
    if (routine) loadRoutineIntoSettings(routine);
  };

  const createNewRoutine = useCallback(() => {
    const newRoutine = createRoutine({
      id: `routine-${Date.now()}`,
      name: `Rutine ${config.routines.length + 1}`,
      title: '✨ Ny rutine',
      hour: 19,
      minute: 0,
      tasks: clone(activeRoutine.tasks),
      children: clone(activeRoutine.children),
    });
    setConfig((prev) => {
      const next = { ...prev, activeRoutineId: newRoutine.id, routines: [...prev.routines, newRoutine] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    loadRoutineIntoSettings(newRoutine);
    navigate('/settings');
  }, [activeRoutine, config.routines.length, loadRoutineIntoSettings, navigate]);

  const removeSelectedRoutine = () => {
    if (config.routines.length <= 1) return;
    const filtered = config.routines.filter((r) => r.id !== setRoutineId);
    const fallback = filtered[0];
    const nextConfig = { ...config, activeRoutineId: fallback.id, routines: filtered };
    setConfig(nextConfig);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextConfig));
    loadRoutineIntoSettings(fallback);
  };

  const addChildDraft = () => setSetChildrenDraft((prev) => [...prev, { name: `Barn ${prev.length + 1}`, icon: '🙂' }]);
  const removeChildDraft = (index) => setSetChildrenDraft((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  const updateChildDraft = (index, key, value) => setSetChildrenDraft((prev) => prev.map((c, i) => (i === index ? { ...c, [key]: value } : c)));

  const getSettingsReturnPath = useCallback(() => {
    if (location.pathname === '/settings' && typeof location.state?.returnTo === 'string') return location.state.returnTo;
    return '/';
  }, [location.pathname, location.state]);

  const cancelSettings = () => navigate(getSettingsReturnPath());

  const saveSettings = () => {
    const hour = Number(setHour);
    const minute = Number(setMinute);
    const tasks = parseTasks(setTasks);
    const childrenDraft = setChildrenDraft
      .map((child, index) => ({ name: String(child.name || '').trim() || `Barn ${index + 1}`, icon: String(child.icon || '').trim() || '🙂' }))
      .filter((child) => child.name);

    const selectedRoutine = config.routines.find((r) => r.id === setRoutineId) || activeRoutine;
    const updatedRoutine = createRoutine({
      ...selectedRoutine,
      title: setTitle.trim() || selectedRoutine.title,
      hour: Number.isFinite(hour) ? Math.max(0, Math.min(23, hour)) : selectedRoutine.hour,
      minute: Number.isFinite(minute) ? Math.max(0, Math.min(59, minute)) : selectedRoutine.minute,
      tasks: tasks.length ? tasks : clone(selectedRoutine.tasks),
      children: childrenDraft.length ? childrenDraft : clone(selectedRoutine.children),
    });

    const nextConfig = {
      ...config,
      activeRoutineId: updatedRoutine.id,
      routines: config.routines.map((r) => (r.id === updatedRoutine.id ? updatedRoutine : r)),
    };
    setConfig(nextConfig);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextConfig));

    navigate(getSettingsReturnPath());
  };

  // ── Routes ────────────────────────────────────────────────────────────────
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Home routines={config.routines} onStartRoutine={startRoutine} onOpenSettings={openSettings} onCreateRoutine={createNewRoutine} />
        }
      />
      <Route
        path="/settings"
        element={
          <SettingsPage
            routines={config.routines}
            selectedRoutineId={setRoutineId}
            setTitle={setTitle}
            setHour={setHour}
            setMinute={setMinute}
            setTasks={setTasks}
            childrenDraft={setChildrenDraft}
            onSelectRoutine={selectRoutineInSettings}
            onAddRoutine={createNewRoutine}
            onRemoveRoutine={removeSelectedRoutine}
            onSetTitle={setSetTitle}
            onSetHour={setSetHour}
            onSetMinute={setSetMinute}
            onSetTasks={setSetTasks}
            onAddChild={addChildDraft}
            onRemoveChild={removeChildDraft}
            onUpdateChildName={(index, value) => updateChildDraft(index, 'name', value)}
            onUpdateChildIcon={(index, value) => updateChildDraft(index, 'icon', value)}
            onCancel={cancelSettings}
            onSave={saveSettings}
          />
        }
      />
      <Route
        path="/routine/:routineId"
        element={
          <RoutinePage
            findRoutineById={findRoutineById}
            activeRoutine={activeRoutine}
            openSettings={openSettings}
          />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
